/**
 * Cognito OAuth code → token exchange landing page.
 *
 * This route runs OUTSIDE the standard Shell because it's only
 * visited mid-redirect; users never navigate here intentionally and
 * the AppHeader's sign-in state would briefly mis-render before the
 * token is stashed. After exchange, we redirect to /.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleFanAuthCallback } from '../auth/fan-auth';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { refreshMyYoutubeMemberships } from '../api/fan-api';

type State = 'working' | 'error';

export function SignInCallback() {
  useDocumentTitle('登入中');
  const navigate = useNavigate();
  const [state, setState] = useState<State>('working');
  const [err, setErr] = useState<string>('');

  const attempt = useCallback(async () => {
    setState('working');
    setErr('');
    try {
      await handleFanAuthCallback();
      // Login-time YT membership refresh — fire-and-forget. Cache
      // gets updated in seconds so the user's next page load sees
      // their current membership tier without waiting for the daily
      // sweep. Errors swallowed inside the helper.
      void refreshMyYoutubeMemberships();
      // Per `nfc-point-cards` — resume a pending point-card redemption the
      // user started while logged out (stashed by the /redeem page).
      const pending = sessionStorage.getItem('pendingRedeemCode');
      if (pending) {
        sessionStorage.removeItem('pendingRedeemCode');
        navigate(`/redeem?key=${encodeURIComponent(pending)}`, { replace: true });
        return;
      }
      navigate('/', { replace: true });
    } catch (e) {
      setState('error');
      setErr((e as Error).message);
    }
  }, [navigate]);

  useEffect(() => { void attempt(); }, [attempt]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-50 px-3 sm:px-4 py-6">
      <div
        className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6 max-w-md w-full space-y-5"
        aria-live="polite"
        aria-busy={state === 'working'}
      >
        {/* Big circular state icon, mirrors the Confirm-page pattern
            so the redirect-loop pair feels consistent. */}
        <div className="flex flex-col items-center text-center">
          {state === 'working' && (
            <div className="relative h-16 w-16 mb-3" aria-hidden="true">
              {/* Soft brand-tinted radial halo behind the spinner so the
                  working state matches the other 3D / loading surfaces
                  in the SPA family. */}
              <span
                className="absolute inset-0 rounded-full blur-md"
                style={{ background: 'radial-gradient(circle, rgba(124,70,243,0.35), transparent 70%)' }}
              />
              <span className="absolute inset-2 rounded-full border-2 border-slate-700/40" />
              <span
                className="absolute inset-2 rounded-full border-2 border-transparent"
                style={{
                  borderTopColor: 'rgb(167, 139, 250)',
                  borderRightColor: 'rgb(56, 189, 248)',
                  animation: 'nx-3d-spin 1.1s linear infinite',
                }}
              />
            </div>
          )}
          {state === 'error' && (
            <div className="h-16 w-16 rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mb-3 text-rose-300 text-3xl">
              ✕
            </div>
          )}
          <h1 className="text-xl sm:text-2xl font-semibold">
            {state === 'working' ? '登入中…' : '登入失敗'}
          </h1>
        </div>

        {state === 'working' && (
          <p className="text-slate-400 text-sm text-center">正在交換 token，請稍候。</p>
        )}

        {state === 'error' && (
          <div role="alert" className="space-y-4">
            <p className="text-slate-400 text-sm break-words text-center">{err}</p>
            <p className="text-slate-500 text-xs text-center">
              常見原因：連結過期、瀏覽器分頁切換造成 state 不符、或網路中斷。重試應該就會好。
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="flex-1 rounded-lg bg-violet-500 hover:bg-violet-400 active:scale-[0.98] text-white font-medium py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-300 shadow-[0_8px_20px_-8px_rgba(124,70,243,0.6)]"
              >重新登入</button>
              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="flex-1 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 active:scale-[0.98] text-slate-200 py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-500"
              >回首頁</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
